/**
 * useUnifiedFilterCatalog.ts
 *
 * The data-fetching layer behind `AspectCatalogMolecule` (specs/unified-filters-v2.md
 * Phase 4 "Catalog data"). `utils/catalogBuilders.ts` supplies the pure
 * schema→`CatalogEntry[]` transforms; this hook is the one place that calls them with
 * real data: the caller's schemas/arc templates for the five non-measure lanes, plus a
 * `POST /search` fetch for the Measures group (`measureRole: 'aggregate'`).
 *
 * Wire-payload contract (spec Phase 4 acceptance): the request carries neither
 * `organizationId` nor `permission` — `/search` resolves the org from the caller's
 * token and defaults `permission` to `'view'` server-side (see
 * `search-service-v1/src/app/routes/search.ts`), so sending either would just restate
 * the server's own default and risks drifting from it.
 */
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useAnalytics } from '@center-technologies/analytics';
import {
  getPermissionEpoch,
  registerPermissionCoalescingMap,
  subscribeToPermissionInvalidation,
} from './permissionInvalidation';
import type { ApiClients } from '../context/ApiClientsContext';
import type { FilterExpression, FilterGroupLeaf } from 'types/filters';
import type { CatalogEntry } from '../components/molecules/AspectCatalog/AspectCatalogMolecule';
import {
  buildAspectCatalogEntries,
  buildArcCatalogEntries,
  buildMeasureCatalogEntries,
  buildPerspectiveCatalogEntries,
  buildRelationshipCatalogEntries,
  mintFilterFromCatalogEntry,
  orderCatalogEntries,
  pushRecentCatalogEntry,
  type MeasureAggregate,
} from '../utils/catalogBuilders';
import type {
  ArcTemplateForFilters,
  BuildPerspectiveOptionsConfig,
  BuildRelationshipOptionsConfig,
  SchemaForFilters,
} from '../utils/filterPropertyBuilder';

type TFn = (key: string, options?: any) => string;

interface AggregateSearchResult {
  id: string;
  label: string;
}

interface AggregateSearchResponse {
  results: AggregateSearchResult[];
}

const EMPTY_AGGREGATES: MeasureAggregate[] = [];

/**
 * Module-scoped cache for the Measures group's aggregate fetch — every catalog
 * instance in one page load (the row picker, the advanced dialog's rail, …) shares
 * one in-flight/settled request instead of firing `/search` per mount. Exported reset
 * per the spec's own requirement ("any catalog cache is module-scoped with an exported
 * reset") so tests, and a genuine "an aggregate was just created" refresh, can clear it.
 *
 * The result is the CALLER's permission-filtered `/search` answer, and `ApiClientsProvider`
 * does not rebuild its clients on an identity switch — so the cache takes part in the
 * shared permission-invalidation epoch: a bump (org switch, impersonation start/end)
 * clears it, and every mounted catalog re-fetches rather than listing aggregates the
 * previous identity could see.
 */
let aggregateCache: Promise<MeasureAggregate[]> | null = null;

export function resetUnifiedFilterCatalogCache(): void {
  aggregateCache = null;
}

registerPermissionCoalescingMap({ clear: resetUnifiedFilterCatalogCache });

function fetchAggregates(apiClients: ApiClients): Promise<MeasureAggregate[]> {
  if (aggregateCache) return aggregateCache;
  const request = apiClients.searchClient
    .post<AggregateSearchResponse>('/search', {
      filterExpressions: [{ property: 'measureRole', operator: 'in', values: ['aggregate'] }],
      limit: 200,
    })
    .then((data) => data.results.map((r) => ({ id: r.id, name: r.label })))
    .catch((err) => {
      // Never cache a failure — the next mount/retry should try again rather than
      // replaying the same rejected promise forever. Only drop THIS request: an
      // invalidation may already have replaced it with one issued for a new identity.
      if (aggregateCache === request) aggregateCache = null;
      throw err;
    });
  aggregateCache = request;
  return request;
}

export interface UseUnifiedFilterCatalogOptions {
  apiClients: ApiClients | null;
  schemas: SchemaForFilters[];
  t: TFn;
  arcTemplates?: Record<string, { template?: ArcTemplateForFilters }>;
  /** In-scope sphere types — drives out-of-scope dimming/absence (`isInScope`). */
  scopeTypeSystemNames?: string[];
  /** True while the picker's search box holds a query — out-of-scope entries are shown (dimmed), not hidden. */
  isSearching?: boolean;
  /** At least one eligible schema declares an arc — gates the single Arc catalog row. */
  hasArcSchemas?: boolean;
  /** Skip the aggregate fetch entirely (e.g. the catalog isn't open yet). Defaults to true. */
  enabled?: boolean;
}

export interface UseUnifiedFilterCatalogReturn {
  /** The full catalog across all six lanes, in `CATALOG_GROUP_ORDER`. */
  entries: CatalogEntry[];
  /** Most-recently-picked entries, newest first, capped — leads the list per spec. */
  recent: CatalogEntry[];
  /** True while the Measures group's aggregate fetch is in flight. */
  loading: boolean;
  /**
   * Stated reason the Measures group is absent (spec
   * `measuresGroupAbsentWithReasonWhenFetchFails`) — `null` once the fetch has
   * succeeded, or before the catalog has fetched at all.
   */
  measuresError: string | null;
  /** Re-fires the aggregate fetch, bypassing the module cache — the catalog's "Try again". */
  retryMeasures: () => void;
  /** Mint the picked entry's filter leaf AND record it in `recent` — the catalog's `onPick`. */
  pickEntry: (entry: CatalogEntry) => FilterExpression | FilterGroupLeaf;
}

/**
 * Builds the catalog-first `CatalogEntry[]` the aspect catalog renders, wiring in the
 * one piece pure functions can't supply: the Measures group's aggregate spheres.
 */
export function useUnifiedFilterCatalog(
  options: UseUnifiedFilterCatalogOptions
): UseUnifiedFilterCatalogReturn {
  const {
    apiClients,
    schemas,
    t,
    arcTemplates,
    scopeTypeSystemNames = [],
    isSearching = false,
    hasArcSchemas = false,
    enabled = true,
  } = options;

  const [recent, setRecent] = useState<CatalogEntry[]>([]);
  // The settled list is stored with the permission epoch it was fetched under, so the
  // render that observes a bump already treats it as absent — no frame in which the
  // previous identity's aggregates are listed while the re-fetch is in flight.
  const [settledAggregates, setSettledAggregates] = useState<{ epoch: number; list: MeasureAggregate[] }>({
    epoch: -1,
    list: [],
  });
  const [loading, setLoading] = useState(false);
  const [measuresError, setMeasuresError] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);
  const logAnalyticsEvent = useAnalytics();
  const permissionEpoch = useSyncExternalStore(
    subscribeToPermissionInvalidation,
    getPermissionEpoch,
    getPermissionEpoch
  );
  const aggregates = settledAggregates.epoch === permissionEpoch ? settledAggregates.list : EMPTY_AGGREGATES;

  useEffect(() => {
    if (!apiClients || !enabled) return;
    let cancelled = false;
    setLoading(true);
    setMeasuresError(null);
    fetchAggregates(apiClients)
      .then((result) => {
        if (cancelled) return;
        setSettledAggregates({ epoch: permissionEpoch, list: result });
      })
      .catch(() => {
        if (cancelled) return;
        setSettledAggregates({ epoch: permissionEpoch, list: [] });
        setMeasuresError(t('filterEditor.catalog.measures.fetchError', { defaultValue: 'Measures could not be loaded.' }));
        // Product-visible signal that the Measures group came up empty for a
        // reason other than "no aggregates exist" — distinguishes a fetch
        // failure from a genuinely empty org in the funnel.
        logAnalyticsEvent('catalog_measures_fetch_failed', undefined, {
          retried: retryToken > 0,
        });
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiClients, enabled, t, retryToken, logAnalyticsEvent, permissionEpoch]);

  const retryMeasures = useCallback(() => {
    resetUnifiedFilterCatalogCache();
    setRetryToken((n) => n + 1);
  }, []);

  const entries = useMemo(() => {
    if (schemas.length === 0) return [];
    const aspect = buildAspectCatalogEntries({
      schemas,
      arcTemplates,
      t,
      groupBy: 'category',
      scopeTypeSystemNames,
      isSearching,
    });
    const perspective = buildPerspectiveCatalogEntries({ schemas, t } as BuildPerspectiveOptionsConfig);
    const relationship = buildRelationshipCatalogEntries({ schemas, t } as BuildRelationshipOptionsConfig);
    const arc = buildArcCatalogEntries(t, hasArcSchemas);
    const measure = buildMeasureCatalogEntries(aggregates, t);
    return orderCatalogEntries([...aspect, ...perspective, ...relationship, ...arc, ...measure]);
  }, [schemas, arcTemplates, t, scopeTypeSystemNames, isSearching, hasArcSchemas, aggregates]);

  const pickEntry = useCallback(
    (entry: CatalogEntry) => {
      setRecent((prev) => pushRecentCatalogEntry(prev, entry));
      // Catalog-first authoring spans six lanes (property/perspective/relationship/
      // arc/measure) from one picker — `lane` is the field usage analysis needs to
      // tell "picked an aspect" from "picked a measure" apart; `filter_property_selected`
      // (FilterEditorPanelMolecule) only covers the legacy property-only picker.
      logAnalyticsEvent('catalog_entry_picked', undefined, {
        lane: entry.lane,
        key: entry.key,
        isOutOfScope: entry.dim ?? false,
      });
      return mintFilterFromCatalogEntry(entry);
    },
    [logAnalyticsEvent]
  );

  return { entries, recent, loading, measuresError, retryMeasures, pickEntry };
}
